<template>
  <v-dialog v-model="showDialog" max-width="690">
    <template v-slot:activator="{props}">
      <v-btn
          v-bind="props"
          variant="elevated"
          color="blue-darken-3"
          prepend-icon="fas fa-download"
      >
        Export Tables to .docx file
      </v-btn>
    </template>
    <v-card>
      <v-alert
          color="red"
          icon="fa fa-warning"
          v-if="selectedAggregationLevels.length === 0" >
        All analyses must have at least one aggregation level.
      </v-alert>
      <v-card-title>
        <v-row>
          <v-col cols="auto">
            <b>Export SII & SPI Analyses tables to .docx file</b>
          </v-col>
          <v-spacer />
          <v-col cols="auto">
            <v-btn icon="fas fa-times" variant="plain" size="x-small" @click="showDialog = false" />
          </v-col>
        </v-row>
      </v-card-title>
      <v-divider/>
      <v-card-text>
        <p>
          <i class="fa fa-download" /> You are about to <b> download a file in a .docx format</b>, which will contain <b>both SII Analysis and SPI Analysis
          tables</b>, as they're depicted in this page.
        </p>
        <br>
        <p>
          <i class="fa fa-list-ul" /> Choose the aggregation levels you want to include in the file. Any <b>aggregation levels</b> you select will be
          <b>included in both tables.</b>
        </p>
        <br><br>
        <v-row>
          <v-col align-self="center" cols='auto'>
            Include Aggregation Levels:
          </v-col>
          <v-spacer/>
          <v-col cols="auto">
            <v-chip-group multiple v-model="selectedAggregationLevels" @update:model-value="selectedAggregationLevels">
              <v-chip v-for="level in aggregationLevels"
                  variant="outlined" elevation="2" filter :value="level"
                  :color="chipColorCoding[level]">
                {{ level }}
              </v-chip>
            </v-chip-group>
          </v-col>
        </v-row>

      </v-card-text>
      <v-divider/>
      <v-card-actions>
        <v-spacer/>
          <v-btn color="red-darken-3" variant="flat" rounded class="px-4" @click="showDialog = false">Cancel</v-btn>
          <v-btn color="blue-darken-4"
                 :prepend-icon="loading? '' : 'fas fa-download'"
                 variant="flat"
                 rounded
                 :disabled="selectedAggregationLevels.length === 0"
                 class="px-6"
                 @click="exportDocument">
            <v-progress-circular class="mx-3" size="small" indeterminate v-if="loading" />
            Download File
          </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>


</template>

<script setup>

  import {onMounted, ref} from "vue";
  import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    Table,
    TableCell,
    TableRow,
    AlignmentType,
    PageOrientation,
    WidthType
  } from "docx";
  import { saveAs } from 'file-saver';
  import { getMarginsBetween, getFirstMargin, getLastMargin, isLikertScale, beautifyAndRoundNumber } from "./KpiAnalysisSiiTable.vue";
  import StringManipulation from "@/Mixins/StringManipulation";

  const props = defineProps({
    projectName: {
      type: String,
      required: true,
    },

    projectId: {
      type: Number,
      required: true
    },

    userEmail: {
      type: String,
      required: true,
    },

    spiTableData: {
      type: Object,
      required: true,
    },

    siiTableData: {
      type: Object,
      required: true,
    }
  })

  const showDialog = ref(false);
  const loading = ref(false);
  const aggregationLevels = ref([]);
  const selectedAggregationLevels = ref([]);

  onMounted(() => {
    // this means "get all the aggregation levels of SII and SPI data in one array, and take the unique values".
    // so if SII has Building, PEB and SPI has Building, City, this array will hold the values:
    // 'Building', 'PEB', 'City'
    aggregationLevels.value = [...Object.keys(props.siiTableData), ...Object.keys(props.spiTableData)].filter((value, index, array) => array.indexOf(value) === index).sort((a, b) => aggregationLevelSorting[a] - aggregationLevelSorting[b]);
    selectedAggregationLevels.value = [...aggregationLevels.value];
  });

  const chipColorCoding = {
    'Building': 'red-darken-2',
    'PEB': 'blue-darken-4',
    'PED': 'green-darken-4',
    'City': 'orange-darken-2',
    'Island': 'purple-darken-3'
  };

  const aggregationLevelSorting = {
    'Building': 0,
    'PEB': 1,
    'PED': 2,
    'City': 3,
    'Island': 4
  };

  const getSiiTable = (aggLevel) => {
    if (!props.siiTableData[aggLevel]) return [];

    const tableHeaderRows = [
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ alignment: "center",  children: [new TextRun({ bold: true, text: "KPI" })]} )] }),
          new TableCell({ children: [new Paragraph({ alignment: "center",  children: [new TextRun({ bold: true, text: "Functional Unit" })]})] }),
          new TableCell({ children: [new Paragraph({ alignment: "center",  children: [new TextRun({ bold: true, text: "Assigned Weight" })]})] }),
          new TableCell({ children: [new Paragraph({ alignment: "center",  children: [new TextRun({ bold: true, text: "Evaluation Scale Indicative Margins" })]})], columnSpan: 5 }),
          new TableCell({ children: [new Paragraph({ alignment: "center",  children: [new TextRun({ bold: true, text: "FU Input" })]})] }),
          new TableCell({ children: [new Paragraph({ alignment: "center",  children: [new TextRun({ bold: true, text: "Score" })]})] }),
          new TableCell({ children: [new Paragraph({ alignment: "center",  children: [new TextRun({ bold: true, text: "Weighted Score" })]})] }),
        ],
      }),
        // extra row for the margins (doesn't work like the vue template)
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph("")] }), // Empty for KPI Column
          new TableCell({ children: [new Paragraph("")] }), // Empty for Functional Unit
          new TableCell({ children: [new Paragraph("")] }), // Empty for Assigned Weight
          new TableCell({ children: [new Paragraph({ alignment: "center", children: [new TextRun({ bold: true, text: '(1)' })] })] }), // MARGINS
          new TableCell({ children: [new Paragraph({ alignment: "center", children: [new TextRun({ bold: true, text: '(2)' })] })] }),
          new TableCell({ children: [new Paragraph({ alignment: "center", children: [new TextRun({ bold: true, text: '(3)' })] })] }),
          new TableCell({ children: [new Paragraph({ alignment: "center", children: [new TextRun({ bold: true, text: '(4)' })] })] }),
          new TableCell({ children: [new Paragraph({ alignment: "center", children: [new TextRun({ bold: true, text: '(5)' })] })] }),
          new TableCell({ children: [new Paragraph("")] }), // Empty for FU Input
          new TableCell({ children: [new Paragraph("")] }), // Empty for Score
          new TableCell({ children: [new Paragraph("")] }), // Empty for Weighted Score
        ],
      }),
    ];

    // this is to make this work just like Vue.JS as much as possible
    // where the first row of every category is the title
    // and the last row is the category score
    const tableDataRows = props.siiTableData[aggLevel].reduce((rows, kpi, index, arr) => {

      // the same checks are perfomed in a v-if template
      const isFirstInDimension = index === 0 || arr[index - 1].dimension_name !== kpi.dimension_name;
      const isLastInDimension = index === arr.length - 1 || arr[index + 1].dimension_name !== kpi.dimension_name;

      // this is the first row which shows the title (dimension)
      if (isFirstInDimension) {
        rows.push(
            new TableRow({
              children: [
                new TableCell({
                  columnSpan: 11,
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `${StringManipulation.methods.clearAndCapitalize(kpi.dimension_name)} Dimension`,
                          bold: true,
                        }),
                      ],
                      alignment: AlignmentType.CENTER,
                    }),
                  ],
                }),
              ],
            })
        );
      }

      // ACTUAL KPI DATA
      rows.push(
          new TableRow({
            children: [
              new TableCell({ children: [new Paragraph(`${index + 1}. ${StringManipulation.methods.clearAndCapitalize(kpi.name)}`)] }),
              new TableCell({ children: [new Paragraph(kpi.functional_unit?.functional_unit ?? "-")] }),
              new TableCell({ children: [new Paragraph(parseFloat(kpi.assigned_weight).toFixed(3))] }),

                // special case for the margins of the kpi (if they exist or are in likert scale)
              ...(kpi.m1 === null && isLikertScale(kpi.functional_unit)
                  ? [
                    new TableCell({ children: [new Paragraph("1")] }),
                    new TableCell({ children: [new Paragraph("2")] }),
                    new TableCell({ children: [new Paragraph("3")] }),
                    new TableCell({ children: [new Paragraph("4")] }),
                    new TableCell({ children: [new Paragraph("5")] }),
                  ]
                  : kpi.m1 === null
                      ? [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [new TextRun({ text: "No margins provided.", italics: true })],
                            }),
                          ],
                          columnSpan: 5,
                        }),
                      ]
                      : [
                        new TableCell({ children: [new Paragraph(getFirstMargin(kpi.m0, kpi.m3))] }),
                        new TableCell({ children: [new Paragraph(getMarginsBetween(kpi.m0, kpi.m1, false))] }),
                        new TableCell({ children: [new Paragraph(getMarginsBetween(kpi.m1, kpi.m2, true))] }),
                        new TableCell({ children: [new Paragraph(getMarginsBetween(kpi.m2, kpi.m3, false))] }),
                        new TableCell({ children: [new Paragraph(getLastMargin(kpi.m3, kpi.m0))] }),
                      ]),

              new TableCell({ children: [new Paragraph(beautifyAndRoundNumber(kpi.functional_unit_input))] }),
              new TableCell({ children: [new Paragraph(kpi.kpi_score.toString())] }),
              new TableCell({ children: [new Paragraph((kpi.kpi_score * kpi.assigned_weight).toFixed(3))] }),
            ],
          })
      );

      // "FINAL SII SCORE" row
      if (isLastInDimension) {
        rows.push(
            new TableRow({
              children: [
                new TableCell({
                  columnSpan: 10,
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `Final SII Score of ${StringManipulation.methods.clearAndCapitalize(kpi.dimension_name)} Dimension:`,
                          bold: true,
                        }),
                      ],
                    }),
                  ],
                }),
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: kpi.dimension_score.toString().includes(".")
                              ? parseFloat(kpi.dimension_score).toFixed(3)
                              : kpi.dimension_score.toString(),
                          bold: true,
                        }),
                      ],
                      alignment: AlignmentType.CENTER,
                    }),
                  ],
                }),
              ],
            })
        );
      }

      return rows;
    }, []);

    // gets returned in a way that the DOCX JS can read.
    return [...tableHeaderRows, ...tableDataRows];
  };

  const getSpiTable = (aggLevel) => {
    if (!props.spiTableData[aggLevel]) return [];

    // table headers
    const tableHeaderRows = [
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ alignment: 'left', children: [ new TextRun({ text: 'KPI', bold: true })] })] }),
          new TableCell({ children: [new Paragraph({ alignment: 'center', children: [ new TextRun({ text: 'Weight', bold: true })] })] }),
          new TableCell({ children: [new Paragraph({ alignment: 'center', children: [ new TextRun({ text: 'Weighted Score', bold: true })] })] }),
        ],
      }),
    ];

    // table data retrieved for each SECTOR that there is
    const tableDataRows = Object.keys(props.spiTableData[aggLevel]).flatMap((sector, sectorIndex) => {
      const sectorData = props.spiTableData[aggLevel][sector];

      if (!sectorData) {
        return [];
      }

      // sector title
      const sectorRows = [
        new TableRow({
          children: [
            new TableCell({
              columnSpan: 3,
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `Sector ${sectorIndex + 1}: ${StringManipulation.methods.clearAndCapitalize(sector)}`,
                      bold: true,
                    }),
                  ],
                  alignment: AlignmentType.CENTER,
                }),
              ],
            }),
          ],
        }),
      ];

      // sector kpi data (these are significantly fewer rows than the SII)
      const kpiRows = sectorData.data.map((kpi, index) =>
          new TableRow({
            children: [
              new TableCell({
                children: [
                  new Paragraph(`${index + 1}. ${StringManipulation.methods.clearAndCapitalize(kpi.name)}`),
                ],
              }),
              new TableCell({
                children: [
                  new Paragraph(parseFloat(kpi.weight).toFixed(3)),
                ],
              }),
              new TableCell({
                children: [
                  new Paragraph(kpi.kpi_score ? (kpi.weight * kpi.kpi_score).toFixed(3) : "-"),
                ],
              }),
            ],
          })
      );

      // sector score
      const sectorScoreRow = new TableRow({
        children: [
          new TableCell({
            columnSpan: 2,
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Final SPI Score of ${StringManipulation.methods.clearAndCapitalize(sector)} Sector:`,
                    bold: true,
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: sectorData.sector_spi_score.toFixed(2),
                    bold: true,
                  }),
                ],
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
        ],
      });

      return [...sectorRows, ...kpiRows, sectorScoreRow];
    });

    return [...tableHeaderRows, ...tableDataRows];
  };

  function exportDocument() {
    loading.value = true;

    const doc = new Document({
      compatibility: {
        version: 15,
        doNotExpandShiftReturn: true,
      },
      sections: [
        {
          properties: {
            page: {
              size: {
                width: 11907, // A4 width in Twips
                height: 16840, // A4 height in Twips
              },
              margin: {
                top: 1440,    // 1 inch
                right: 1440,  // 1 inch
                bottom: 1440, // 1 inch
                left: 1440,   // 1 inch
              },
              orientation: PageOrientation.PORTRAIT, // Ensure A4 portrait mode
            },
          },
          children: [
            new Paragraph({
              heading: 'Heading1',
              alignment: 'center',
              children: [
                new TextRun({
                    text: "USE Web App SII & SPI Analysis",
                    bold: true,
                    }
                ),
              ],
            }),

            new Paragraph({
              children: []
            }),

            new Paragraph({
              alignment: 'left',
              children: [
                new TextRun({
                      text: `Project Name: `
                    }
                ),
                new TextRun({
                    text: `${props.projectId}. ${props.projectName}`,
                  bold: true
                })
              ],
            }),

            new Paragraph({
              children: []
            }),

            new Paragraph({
              alignment: 'left',
              children: [
                new TextRun({
                      text: `Date Issued: `
                    }
                ),
                new TextRun({
                  text: new Date().toDateString(),
                  bold: true,
                })
              ],
            }),

            new Paragraph({
              children: []
            }),

            new Paragraph({
              alignment: 'left',
              children: [
                new TextRun({
                      text: `User E-Mail: `
                    }
                ),
                new TextRun({
                  text: props.userEmail,
                  bold: true
                })
              ],
            }),

            new Paragraph({
              children: []
            }),

            new Paragraph({
              alignment: 'left',
              children: [
                new TextRun({
                    text: "This file was extracted from the Sustainability Impact Index and Sustainability Performance Index analysis, from the USE Web Application. ",
                  }
                ),
              ],
            }),

            new Paragraph({
              children: []
            }),

            // --- SII Analysis Table --- //
            new Paragraph({
              alignment: 'center',
              heading: 'Heading2',
              children: [
                new TextRun({
                      text: "SII Analysis",
                    }
                ),
              ],
            }),

            new Paragraph({
              children: [
                new TextRun({
                    text: "The following table is about the Sustainability Impact Index indicators."
                  }
                ),
              ],
            }),
            new Paragraph({
              children: []
            }),

            ...selectedAggregationLevels.value.flatMap((aggLevel) => {
              const lineBreak = new Paragraph({
                children: []
              });

              const paragraph = new Paragraph({
                heading: 'Heading3',
                children: [
                    new TextRun(`${aggLevel} Level:`)
                ]
              });

              const table = new Table({
                width: {
                  size: 10000, // 100% width
                  type: WidthType.DXA,
                },
                columnWidths: [1050, 1000, 1000, 700, 700, 700, 700, 700, 800, 800, 850],
                rows: getSiiTable(aggLevel)
              });

              return [lineBreak, paragraph, table];
            }),

            new Paragraph({
              children: []
            }),

            // --- SPI ANALYSIS TABLE --- //
            new Paragraph({
              alignment: 'center',
              heading: 'Heading2',
              children: [
                new TextRun({
                      text: "SPI Analysis",
                    }
                ),
              ],
            }),

            new Paragraph({
              children: [
                new TextRun({
                      text: "The following table is about the Sustainability Performance Index indicators."
                    }
                ),
              ],
            }),

            ...selectedAggregationLevels.value.flatMap((aggLevel) => {
              const lineBreak = new Paragraph({
                children: []
              });

              const paragraph = new Paragraph({
                heading: 'Heading3',
                children: [
                  new TextRun(`${aggLevel} Level:`)
                ]
              });

              const table = new Table({
                width: {
                  size: 10000, // 100% width
                  type: WidthType.DXA,
                },
                columnWidths: [6500, 1000, 2000],
                rows: getSpiTable(aggLevel)
              });

              return [lineBreak, paragraph, table];
            }),


          ],
        },
      ],
    });

    // downloads the file
    Packer.toBlob(doc).then((blob) => {
      saveAs(blob, `${props.projectName}_analytical_SII_SPI_table_${(new Date()).toISOString()}.docx`);
    });

    loading.value = false;
  }

</script>

<style scoped>

</style>